/**
 * What the Search Progress tab is showing, as a pure function of the report and
 * three URL parameters.
 *
 * EXTRACTED 2026-08-21, and the reason is a bug that had already shipped. The
 * tab computed this inline; the export re-derived it and quietly ignored
 * `?from`, `?to` and `?bucket`, so a reader who narrowed the range to April
 * downloaded a document covering the whole year and had no way to tell. The
 * charts avoided that class of drift by being shared components — the ARITHMETIC
 * behind them had no such protection until this file existed.
 *
 * Both callers now read the same answer from the same place, so "the export
 * shows what the tab shows" is true by construction rather than by two
 * implementations agreeing.
 *
 * ORDER IS LOAD-BEARING inside `viewFor`: slice, then bucket, then fold.
 * Bucketing first puts the range's edges in the middle of a week; folding first
 * makes each bucket a sum of running totals. That ordering came from the tab and
 * is the thing most worth not re-deriving by hand.
 */

import type { SearchReport, SearchReportDay } from "@nu-assist/contracts";

import { dayLabel } from "./dayAxis";

import {
  type StageProgress,
  type StageSeries,
  baselineTotal,
  cumulativeFrom,
  stageProgressFrom,
} from "./stageProgress";
import {
  type Granularity,
  type RangeSelection,
  bucketLabel,
  bucketOutreach,
  bucketProgress,
  isGranularity,
  resolveRange,
  sliceOutreach,
  sliceProgress,
} from "./timeRange";

export interface ProgressView {
  /** The stage series after slicing and bucketing — what the two line charts plot. */
  daily: StageProgress;
  /** The running-total series. Empty when there is no range to compute one over. */
  cumulative: StageSeries[];
  outreachDays: SearchReportDay[];
  /** Outreaches the range excluded, on top of the report's own out-of-window count. */
  droppedOutreach: number;
  /** Stage entries that happened before the visible range, already in the totals. */
  carriedIn: number;
  /**
   * The baseline of the UNFILTERED report — how much the 366-day cap cut off.
   *
   * Separate from `carriedIn`, which grows with whatever the reader just
   * filtered out. Only this one is a fact about the report rather than about the
   * current range, and it is what tells the tab the cap is actually biting.
   */
  unfilteredCarriedIn: number;
  granularity: Granularity;
  /**
   * The resolved range, or null when the report has nothing to plot at all.
   *
   * `RangeSelection` rather than a bare `{from, to}`: it also carries the
   * `notice` a clamped or rejected request produces, and the tab renders that.
   */
  range: RangeSelection | null;
  /** The full span the report carries, before any range was applied. */
  bounds: { first: string; last: string } | null;
  /** True when the reader narrowed the range away from the full span. */
  filtered: boolean;
  /** Names one x-axis point the way the charts label it. */
  pointLabel(day: string): string;
}

/**
 * Read the granularity out of a URL parameter, defaulting to `week`.
 *
 * An unrecognised value reads as the default rather than as an error — the same
 * convention every other parameter on this page follows, for the same reason:
 * these arrive in links people send each other. Since 2026-08-27 `?bucket=day`
 * is one of those unrecognised values: it was the default until the switch lost
 * its day option, so every link written before then lands on `week` rather than
 * on a granularity the UI can no longer offer or undo.
 */
export function granularityFrom(raw: string | null): Granularity {
  return isGranularity(raw) ? raw : "week";
}

/**
 * The view for one report and one set of parameters.
 *
 * `from` and `to` absent mean the full span, so a reader who never touches the
 * pickers gets everything and the canonical URL stays bare.
 */
export function viewFor(
  report: SearchReport,
  params: { from?: string | null; to?: string | null; bucket?: string | null }
): ProgressView {
  const progress = stageProgressFrom(
    report.stage_transitions_by_day ?? [],
    report.stage_transitions_before_window ?? {}
  );
  const outreachActivity = report.outreach_activity ?? [];

  // The union of both spans, because the pickers filter all three charts at
  // once. Bounding to the stage series alone would make an outreach in a month
  // with no stage movement unreachable — and on the real search this was
  // validated against, the two genuinely do not cover the same months.
  const allDays = [...progress.days, ...outreachActivity.map((entry) => entry.day)].sort();
  const bounds =
    allDays.length === 0 ? null : { first: allDays[0], last: allDays[allDays.length - 1] };

  const granularity = granularityFrom(params.bucket ?? null);
  const range = bounds ? resolveRange(params.from ?? null, params.to ?? null, bounds) : null;
  const filtered =
    range !== null && bounds !== null
      ? range.from !== bounds.first || range.to !== bounds.last
      : false;

  if (range === null) {
    return {
      daily: progress,
      cumulative: [],
      outreachDays: bucketOutreach(outreachActivity, granularity),
      droppedOutreach: 0,
      carriedIn: baselineTotal(progress),
      unfilteredCarriedIn: baselineTotal(progress),
      granularity,
      range,
      bounds,
      filtered,
      pointLabel: (day) => bucketLabel(day, granularity),
    };
  }

  const sliced = sliceProgress(progress, range.from, range.to);
  const daily = bucketProgress(sliced, granularity);
  const outreach = sliceOutreach(outreachActivity, range.from, range.to);
  return {
    daily,
    cumulative: cumulativeFrom(daily),
    outreachDays: bucketOutreach(outreach.days, granularity),
    droppedOutreach: outreach.droppedBefore,
    carriedIn: baselineTotal(daily),
    unfilteredCarriedIn: baselineTotal(progress),
    granularity,
    range,
    bounds,
    filtered,
    pointLabel: (day) => bucketLabel(day, granularity),
  };
}

/**
 * The view's own state, in words, for a downloaded document.
 *
 * The pickers are CONTROLS and cannot come along, but what they were set to is
 * information — and a file that covers April while looking like it covers the
 * year is the specific failure that made this module exist.
 */
export function describeProgressView(view: ProgressView): string {
  const grain = view.granularity === "week" ? "by week" : "by month";
  if (view.range === null) return `Nothing to plot, ${grain}.`;
  if (!view.filtered) return `The whole window the report keeps, ${grain}.`;
  // `dayLabel`, not `bucketLabel`: the two ENDS of the range are calendar days
  // whatever the buckets are, and naming them "Week of…" would describe a range
  // the reader did not set. `bucketLabel(x, "day")` used to say this, until the
  // switch lost its day option.
  return `${dayLabel(view.range.from)} to ${dayLabel(view.range.to)}, ${grain}.`;
}
